import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import {
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Grow,
  Icon,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";

import * as homeAPI from "../../api";
import IndicatorDock from "./IndicatorDock";
import { commonIcon, useStyles } from "./HomeCardStyles";
import { useRequestHeaders } from "../../../../core/hooks/useRequestHeaders";
import {
  DASHBOARD_CARD_EXTRA,
  DASHBOARD_CARD_EXTRA_LARGE,
  DASHBOARD_CARD_LARGE,
  DASHBOARD_CARD_MEDIUM,
  DASHBOARD_CARD_MEGA_LARGE,
  DASHBOARD_CARD_SMALL,
  FORM_OPERATION_NEW,
  FORM_OPERATION_VIEW,
  FORM_OPERATON_EDIT,
  INITIAL_OPEN_EXTERNAL_FORM_STATE,
} from "../../../../util/Constants";
import KpiResolver, { KPI_TYPE_CARD } from "../kpis/KpiResolver";
import useIsMounted from "react-is-mounted-hook";
import { useAccessesStore } from "../../../../core/stores/AccessesStore";
import OptionsCard from "./OptionsCard";
import { find, isEmpty, isNil, toLower, trim } from "lodash";
import ExternalFormContainer from "../../../../components/forms/externalFormContainer/ExternalFormContainer";
import ExternalFormContainerValues from "../../../../components/forms/externalFormContainer/ExternalFormContainerValues";
import ExternalFormContainerLikeRecordsPanel from "../../../../components/forms/externalFormContainer/ExternalFormContainerLikeRecordsPanel";
import { useExternalReportsStore } from "../../../../core/stores/ExternalReportsStore";
import { resolveCoordtinationFiltersForExternalQuery } from "../../../../components/reportsContainer/util-reports";
import { useInteractiveItemStore } from "../../../../core/stores/InteractiveItemStore";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { WorkspaceContext } from "../../../../core/providers/WorkspaceContext";
import { useTranslation } from "react-i18next";

export default function IndicatorCard(props) {
  const {
    kpiName,
    item,
    index,
    cardSize,
    sectionIndex,
    sectionHidden,
    itemsLength,
    loadOnRender,
  } = props;
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [fetching, isFetching] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [openExternalForm, setOpenExternalForm] = useState(
    INITIAL_OPEN_EXTERNAL_FORM_STATE
  );
  const isMounted = useIsMounted();

  const cardSizeToTake = item?.itemSize || cardSize;

  const hidden = item?.hidden || sectionHidden || false;

  const hasDetail = data?.hasDetail ?? true;

  // openFormDef
  const openFormDef = data?.openFormDef;
  const hasOpenFormDef = !isNil(openFormDef) && !isEmpty(openFormDef);
  // openQueryDef
  const openQueryDef = data?.openQueryDef;
  const hasOpenQueryDef = !isNil(openQueryDef) && !isEmpty(openQueryDef);
  const needToOpenInExternalQuery = data?.openQueryInExternalMode === true;

  const setOpenExternalQuery = useExternalReportsStore(
    (state) => state.setOpenExternalQuery
  );

  const classes = useStyles({
    indColor: data?.color,
    cardSize: cardSizeToTake,
    hidden,
  });

  const REQUEST_HEADERS = useRequestHeaders();
  const isEditing = useAccessesStore((state) => state.isEditing);

  const flattenedMenuItems = useAccessesStore(
    (state) => state.flattenedMenuItems
  );

  const history = useHistory();
  const { addTabItem, addSubItem, changeActiveTabItem, changeActiveSubItem } =
    useInteractiveItemStore();
  const { addToFormHistory } = useContext(WorkspaceContext);

  const shouldFetchByRender = isNil(loadOnRender) || loadOnRender === true;

  useEffect(() => {
    if (isMounted() && shouldFetchByRender === true) {
      isFetching(true);
      fetchKPI();
    }

    return () => null;
    // eslint-disable-next-line
  }, [loadOnRender]);

  async function fetchKPI() {
    const response = await homeAPI.getKpi({
      REQUEST_HEADERS,
      kpiName,
    });

    if (response) {
      const { dataResponse } = response;
      setData(dataResponse);
    }

    isFetching(false);
  }

  const handleDetailDock = useCallback(
    (open) => {
      if (isEditing) {
        return;
      }
      setOpenDetail(open);
    },
    [isEditing]
  );

  function resolveCardClassName(cs) {
    if (cs === DASHBOARD_CARD_MEGA_LARGE) {
      return classes.megaLargeRootCard;
    } else if (cs === DASHBOARD_CARD_EXTRA_LARGE) {
      return classes.extraLargeRootCard;
    } else if (cs === DASHBOARD_CARD_EXTRA) {
      return classes.extraRootCard;
    } else if (cs === DASHBOARD_CARD_LARGE) {
      return classes.largeRootCard;
    } else if (cs === DASHBOARD_CARD_MEDIUM) {
      return classes.mediumRootCard;
    } else {
      return classes.smallRootCard;
    }
  }

  function handleRefreshKpi(e) {
    if (isEditing) {
      return;
    }

    isFetching(true);
    e.preventDefault();
    e.stopPropagation();
    fetchKPI();
  }

  const handleClickOnKpi = useCallback(() => {
    if (isEditing) {
      return;
    }

    if (hasOpenFormDef === true) {
      const useUkey = openFormDef?.useUkey;
      if (useUkey === true) {
        const ukey = openFormDef?.params?.ukey;
        setOpenExternalForm({
          open: true,
          operation: openFormDef?.defaultOperation,
          path: openFormDef?.path,
          idForm: openFormDef?.idForm,
          overrideOps: openFormDef?.formOperations || [],
          ukey,
        });
      } else {
        const isNewOperation =
          trim(toLower(openFormDef?.defaultOperation)) ===
          trim(toLower(FORM_OPERATION_NEW));
        if (isNewOperation) {
        } else {
          setOpenExternalForm({
            open: true,
            operation: openFormDef?.defaultOperation,
            path: openFormDef?.path,
            idForm: openFormDef?.idForm,
            overrideOps: openFormDef?.formOperations || [],
            values: openFormDef?.params,
          });
        }
      }
    } else if (hasOpenQueryDef && !isNil(openQueryDef?.queryKey)) {
      const queryKey = openQueryDef?.queryKey;
      if (needToOpenInExternalQuery === true) {
        const newOpenExternalQueryState = {
          open: true,
          queryKey: queryKey,
          coordinationFilters: resolveCoordtinationFiltersForExternalQuery({
            row: null,
            coordinationFiltersDef: openQueryDef?.coordinationFilters,
          }),
          defaultWidth: openQueryDef?.defaultWidth || null,
          defaultHeight: openQueryDef?.defaultHeight || null,
        };
        setOpenExternalQuery(newOpenExternalQueryState);
      } else {
        const menuItemDefinition = find(flattenedMenuItems, (obj) => {
          const regex = new RegExp(`/consultas/.*/${queryKey}$`, "i");
          return regex.test(obj.route);
        });

        if (!isNil(menuItemDefinition)) {
          const itemToOpen = {
            route: menuItemDefinition.route,
            defaultPinned: false,
            final: false,
            propText: menuItemDefinition.propText,
            name: menuItemDefinition.name,
          };
          addToFormHistory(itemToOpen);
          addTabItem(itemToOpen);
          const sub = {
            ...itemToOpen,
            parentId: itemToOpen.route,
            propText: t("MULTI_ITEM_SAVED_RECORDS"),
          };
          addSubItem(sub);
          changeActiveTabItem(itemToOpen.route);
          changeActiveSubItem(sub);
          history.push(itemToOpen.route, {
            formName: queryKey,
          });
        }
      }
    } else if (hasDetail) {
      setOpenDetail(true);
    }
  }, [
    isEditing,
    hasOpenFormDef,
    hasOpenQueryDef,
    openQueryDef,
    hasDetail,
    openFormDef,
    needToOpenInExternalQuery,
    flattenedMenuItems,
    addTabItem,
    changeActiveTabItem,
    history,
    setOpenExternalQuery,
    addToFormHistory,
    addSubItem,
    changeActiveSubItem,
    t,
  ]);

  function handleBackFromExternalThingOpenedByKpi() {
    setOpenExternalForm(INITIAL_OPEN_EXTERNAL_FORM_STATE);
  }

  const isCardGraphic = useMemo(() => {
    return data?.tipo_grafico === KPI_TYPE_CARD;
  }, [data]);

  return (
    <Grow in={true} timeout={isEditing ? 0 : 300 * index + 1000}>
      <Grid item key={index + 100}>
        {openDetail && (
          <IndicatorDock
            openDetail={openDetail}
            handleDetailDock={handleDetailDock}
            kpi={data}
            kpiName={kpiName}
          />
        )}

        {!isNil(openExternalForm) && openExternalForm.open === true ? (
          !isNil(openExternalForm.ukey) ? (
            <ExternalFormContainer
              openModal={openExternalForm.open}
              handleOpenModalForm={() =>
                handleBackFromExternalThingOpenedByKpi()
              }
              ukey={openExternalForm.ukey}
              operation={openExternalForm.operation || FORM_OPERATION_VIEW}
              path={openExternalForm.path}
              idForm={openExternalForm.idForm}
              values={openExternalForm.values}
              overrideOps={openExternalForm.overrideOps}
            />
          ) : trim(toLower(openExternalForm.operation)) ===
            trim(toLower(FORM_OPERATION_NEW)) ? (
            <ExternalFormContainerLikeRecordsPanel
              openModal={openExternalForm?.open}
              handleOpenModalForm={() =>
                handleBackFromExternalThingOpenedByKpi()
              }
              operation={FORM_OPERATION_NEW}
              path={openExternalForm?.path}
              idForm={openExternalForm?.idForm}
              openFormLikeRecordsPanel={true}
              solution={openExternalForm?.params?.solution}
              tableName={openExternalForm?.params?.tableName}
              defaultWidth={openExternalForm?.params?.defaultWidth}
              defaultHeight={openExternalForm?.params?.defaultHeight}
              handleBack={() => handleBackFromExternalThingOpenedByKpi()}
            />
          ) : (
            <ExternalFormContainerValues
              openModal={openExternalForm.open}
              handleOpenModalForm={() =>
                handleBackFromExternalThingOpenedByKpi()
              }
              operation={openExternalForm.operation || FORM_OPERATON_EDIT}
              path={openExternalForm.path}
              idForm={openExternalForm.idForm}
              values={openExternalForm.values}
              overrideOps={openExternalForm.overrideOps}
            />
          )
        ) : null}

        <Tooltip
          title={data?.tooltip || ""}
          disableHoverListener={isEditing || isCardGraphic}
        >
          <Card
            key={item.key}
            className={resolveCardClassName(cardSizeToTake)}
            onClick={() => handleClickOnKpi()}
            style={{
              cursor: hasDetail ? "pointer" : "default",
              border:
                !hasDetail || isCardGraphic || fetching ? "none" : undefined,
            }}
          >
            <CardContent className={classes.cardContent}>
              {fetching ? (
                <div className={classes.fetchingContainer}>
                  <CircularProgress />
                </div>
              ) : !fetching && data ? (
                <div className={classes.cardContentInd}>
                  <KpiResolver
                    type={data?.tipo_grafico}
                    id={`${kpiName}_${index}`}
                    data={data}
                    cardSize={cardSizeToTake}
                  />
                  {isCardGraphic ? null : (
                    <>
                      <Typography
                        variant={
                          DASHBOARD_CARD_SMALL !== cardSizeToTake
                            ? "h5"
                            : "body1"
                        }
                        className={classes.kpiNameTypo}
                      >
                        {data?.nombre_kpi}
                      </Typography>
                      <IconButton
                        onClick={handleRefreshKpi}
                        className={classes.indRefreshBtn}
                      >
                        <Icon
                          className="fas fa-sync"
                          style={{
                            ...commonIcon,
                            fontSize:
                              DASHBOARD_CARD_SMALL !== cardSizeToTake ? 16 : 14,
                          }}
                        />
                      </IconButton>
                    </>
                  )}
                </div>
              ) : !shouldFetchByRender ? null : (
                <Typography>Error: {kpiName || ""}</Typography>
              )}
              {isEditing && !isCardGraphic && (
                <OptionsCard
                  currentSize={cardSizeToTake}
                  cardIndex={index}
                  sectionIndex={sectionIndex}
                  isKpi={true}
                  sectionHidden={sectionHidden}
                  hidden={hidden}
                  itemsLength={itemsLength}
                />
              )}
            </CardContent>
          </Card>
        </Tooltip>
      </Grid>
    </Grow>
  );
}
